<script setup>
import { Badge } from '@dashhudson/dashing-ui/badge';
import { Column, DataTable } from '@dashhudson/dashing-ui/data-table';
import { Section } from '@dashhudson/dashing-ui/section';
import { SectionHeader } from '@dashhudson/dashing-ui/section-header';
import orderBy from 'lodash/orderBy';
import { computed, ref } from 'vue';

import {
  SENTIMENT_COLORS,
  SOCIAL_LISTENING_SENTIMENT_SUMMARY_TOOLTIPS,
  TOPIC_COMPARISON_MAX_TOPIC_COUNT,
} from '@/app/socialListening/constants';
import { determineNetSentimentSeverity } from '@/app/socialListening/utils/metric-utils';
import { useSocialListeningComparisonsStore } from '@/stores/social-listening-comparisons';
import { formatPercentage } from '@/utils/formatters';

const comparisonStore = useSocialListeningComparisonsStore();

const sortField = ref();
const sortOrder = ref();

const tableData = computed(() => {
  if (!comparisonStore.filteredTopics) return [];

  return comparisonStore.filteredTopics.map((topic) => {
    const topicStats = comparisonStore.topicsStats[topic.name] || {};

    const netSentiment = Number(topicStats.netSentiment) || 0;
    const positiveSentiment = Number(topicStats.positiveSentiment) || 0;
    const neutralSentiment = Number(topicStats.neutralSentiment) || 0;
    const negativeSentiment = Number(topicStats.negativeSentiment) || 0;
    const totalSentiment = positiveSentiment + neutralSentiment + negativeSentiment;

    return {
      topicName: topic.name,
      positive: {
        value: totalSentiment ? positiveSentiment / totalSentiment : '-',
        color: SENTIMENT_COLORS.POSITIVE,
      },
      neutral: {
        value: totalSentiment ? neutralSentiment / totalSentiment : '-',
        color: SENTIMENT_COLORS.NEUTRAL,
      },
      negative: {
        value: totalSentiment ? negativeSentiment / totalSentiment : '-',
        color: SENTIMENT_COLORS.NEGATIVE,
      },
      netSentiment: {
        value: totalSentiment ? netSentiment : '-',
        severity: determineNetSentimentSeverity(netSentiment),
      },
    };
  });
});

const sortedTableData = computed(() => {
  if (!sortField.value || !sortOrder.value) {
    return tableData.value;
  }

  return orderBy(
    tableData.value,
    [
      (row) => {
        const fieldValue = row[sortField.value];
        const value = fieldValue.value ? fieldValue.value : fieldValue;
        return value === '-' ? -Infinity : value;
      },
    ],
    [sortOrder.value.toLowerCase()],
  );
});
</script>

<template>
  <Section>
    <SectionHeader title="Sentiment Analysis" />
    <DataTable
      v-model:sort-field="sortField"
      v-model:sort-order="sortOrder"
      :value="sortedTableData"
      :loading="comparisonStore.pendingDataPull.topicsStats"
      :num-loading-items="
        comparisonStore.selectedComparison?.topics?.length || TOPIC_COMPARISON_MAX_TOPIC_COUNT
      "
      removable-sort
    >
      <template v-if="!comparisonStore.pendingDataPull.topicsStats">
        <Column field="topicName" header="Topic" sortable class="w-48">
          <template #body="{ data }">
            <p class="dash-typo-base-medium">{{ data.topicName }}</p>
          </template>
        </Column>

        <Column
          field="positive"
          header="Positive Sentiment"
          :tooltip="SOCIAL_LISTENING_SENTIMENT_SUMMARY_TOOLTIPS.POSITIVE"
          sortable
          class="w-56"
        >
          <template #body="{ data }">
            <div class="flex items-center gap-2">
              <Badge dot severity="success" />
              <span class="dash-typo-base-medium">{{ formatPercentage(data.positive.value) }}</span>
            </div>
          </template>
        </Column>

        <Column
          field="neutral"
          header="Neutral Sentiment"
          :tooltip="SOCIAL_LISTENING_SENTIMENT_SUMMARY_TOOLTIPS.NEUTRAL"
          sortable
          class="w-56"
        >
          <template #body="{ data }">
            <div class="flex items-center gap-2">
              <Badge dot severity="neutral" />
              <span class="dash-typo-base-medium">{{ formatPercentage(data.neutral.value) }}</span>
            </div>
          </template>
        </Column>

        <Column
          field="negative"
          header="Negative Sentiment"
          :tooltip="SOCIAL_LISTENING_SENTIMENT_SUMMARY_TOOLTIPS.NEGATIVE"
          sortable
          class="w-56"
        >
          <template #body="{ data }">
            <div class="flex items-center gap-2">
              <Badge dot severity="negative" />
              <span class="dash-typo-base-medium">{{ formatPercentage(data.negative.value) }}</span>
            </div>
          </template>
        </Column>

        <Column
          field="netSentiment"
          header="Net Sentiment"
          :tooltip="SOCIAL_LISTENING_SENTIMENT_SUMMARY_TOOLTIPS.NET_SENTIMENT"
          sortable
          class="w-56"
        >
          <template #body="{ data }">
            <div class="flex items-center gap-2">
              <Badge dot :severity="data.netSentiment.severity" />
              <span class="dash-typo-base-medium">{{ data.netSentiment.value }}</span>
            </div>
          </template>
        </Column>
      </template>
    </DataTable>
  </Section>
</template>
